// ============================================================
// DraftEditor.tsx — (B4, batch 2 read-first) the statement DRAFT:
//   • the statement details (dates, kind, retention, signed by, payee +
//     detail, proposed tick): a read-only summary with a pencil; Save /
//     Cancel only while editing (leaving with unsaved changes asks first —
//     M16; Save waits while the retention box holds invalid text — M11). A
//     NEW statement opens straight into the editor;
//   • once prepared / approved the details are LOCKED (no pencil) and "Send
//     back for changes" (#statement-send-back — the Settlement page's lock
//     box focuses it) withdraws that, with a reason;
//   • a child's / protected party's payee is never "To the client";
//   • "Before it can be issued": the shared ActionBox, each line's button
//     DOES the fix (prepare, approve, open the court order / the exact field,
//     else the page's onFix). A refused issue lists its reasons the same way
//     (the only live box);
//   • maker → checker → Issue; Approve asks first (ConfirmDialogue) and
//     sends the notes shown; a disabled button says why in visible text.
// (Audit fix batch 1, 31 Oct)
//   • H3: Save sends the version the editor OPENED from and only the fields
//     that changed — another user's change meanwhile → 409, never overwritten;
//   • H2: the final bill differs part by part → Issue waits for "I've checked
//     the difference" + a reason (MismatchCheck), sent with the issue;
//   • M5: "Mark as prepared" waits while the Settlement page has unsaved figures;
//   • M1: the preparation was withdrawn (figures changed after it) → said why.
// (Settlement redesign)
//   • ONE main button, in the page's sticky action bar with the step trail under
//     it; its label comes from nextStep. Blockers sit at the top of the bar,
//     directly above it; Preview / Send back are quiet;
//   • "Approve and issue" runs the same prepare, approve and issue routes in
//     order, under one confirm. If issuing is refused the approval stands and
//     the button becomes "Issue statement";
//   • the details save themselves as you type; Done closes them. A refused save
//     stops until the next edit and says why, with "Reload their version" on a
//     conflict and "Discard my change" whenever something is unsaved;
//   • either date missing: "Add the dates", both side by side (DatesEditor);
//   • the notes are a one-line checklist (ApproverNotes); no figure tiles (the
//     money column is the one set of figures).
// (Audit B2)
//   • low b: the bill difference at issue is recorded ("I've checked the
//     difference" + reason), OK'd by an approver, and issued by someone else
//     (IssueMismatchCheck); Issue says why it waits;
//   • H3: "Record the court's decision" focuses the court steps; a corrected
//     version's court-order form starts from the previous version's order.
// ============================================================
import React, { useEffect, useId, useRef, useState } from 'react'
import {
  DESTINATION_DETAIL_LABEL, DESTINATION_LABEL,
  type BlockReason, type CaseStatements, type DraftFields, type FixTarget, type StatementDraft,
} from '../../../types/settlementStatement.types'
import type { NetDestination } from '../../../types/settlement.types'
import { B_PRIMARY, B_SECONDARY, BIcon, INPUT, NOTE, money } from '../../billing/billingUi'
import { APPROVE_AND_ISSUE, canDoItAll, datesMissing, issueHeldByBill, nextStepOf, notesOutOfDate, realBlockers, type NextStep } from './nextStep'
import ApproverNotes from './ApproverNotes'
import DatesEditor from './DatesEditor'
import { Banner, PenceField, useBadFields } from '../settlementUi'
import { ErrorNote, type ActionBoxAction, type ActionBoxItem } from '../../shared/ActionBox'
import DateInput from '../../shared/DateInput'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import ConfirmDialogue from '../../shared/ConfirmDialogue'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'
import { ukDate } from '../../../utils/civilDate'
import { BarRows, EditPencil, Fold, StepTrail, SummaryRow, focusById, knownChildOrPp } from './statementBits'
import { COURT_ORDER_FIRST_FIELD, COURT_STEPS_ID, CourtOrderPanel, type RecordOrder, type RecordStage } from './CourtApproval'
import { CarriedDisputeNote, inSlot, type MainSlot } from './ClientAnswer'
import SendBackDialog from './SendBackDialog'
import { IssueMismatchCheck } from './MismatchCheck'

const DESTINATIONS: NetDestination[] = ['CLIENT', 'COURT_FUNDS_OFFICE', 'CHILD_ACCOUNT', 'DEPUTY']
const SELECT = `${INPUT} pr-8`
const QUIET = 'text-[13px] font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2 disabled:opacity-50'
export const SEND_BACK_ID = 'statement-send-back'

// The detail fields a blocker can point at → their element ids.
type FieldTarget = 'settlementDate' | 'damagesReceivedAt' | 'signatory' | 'netDestination' | 'retentionReason'
const FIELD_ID: Record<FieldTarget | 'kind', string> = {
  settlementDate: 'statement-field-settlement-date',
  damagesReceivedAt: 'statement-field-received',
  signatory: 'statement-field-signatory',
  netDestination: 'statement-field-destination',
  retentionReason: 'statement-field-retention-reason',
  kind: 'statement-field-kind',
}
const FIELD_TARGETS = new Set<FixTarget>(['settlementDate', 'damagesReceivedAt', 'signatory', 'netDestination', 'retentionReason', 'statement'])

export interface DraftProps {
  data: CaseStatements
  draft: StatementDraft | null
  busy: string | null
  onFix: (t: FixTarget) => void
  save: (body: DraftFields) => Promise<boolean>
  // Any edit of a detail (a save's refusal is cleared once a field changes).
  onEdit?: () => void
  prepare?: () => Promise<boolean>
  // They can do it all: prepare + approve (+ issue when andIssue) in one go.
  prepareAndApprove?: (review?: { notesReviewed: boolean; noteCodes: string[]; notes: { code: string; message: string }[] }, andIssue?: boolean) => Promise<boolean>
  askApproval?: () => Promise<boolean>
  // Who was asked just now; the server's refusal of a step, shown beside it.
  asked?: string[] | null
  actionError?: string | null
  check?: (review?: { notesReviewed: boolean; noteCodes: string[]; notes: { code: string; message: string }[] }, andIssue?: boolean) => Promise<boolean>
  issue?: () => void
  // (Audit B2, low b) The bill difference: record the check / an approver OKs it.
  checkMismatch?: (reason: string) => Promise<boolean>
  approveMismatch?: () => Promise<boolean>
  // (Audit fix batch 1, M5) Unsaved figures elsewhere on the page (why Prepare waits).
  figuresUnsaved?: string | null
  preview?: () => void
  recordOrder?: RecordOrder
  recordStage?: RecordStage
  sendBack?: (reason: string) => Promise<boolean>
  // (Audit fix batch 2, M7) A refused send-back — shown INSIDE its dialog.
  sendBackError?: string | null
  // The server's reasons for a refused issue (shown live, at the top).
  blockedOnIssue?: BlockReason[] | null
  onCancel?: () => void
  // (Settlement redesign) The sentence + main button, worked out once by the panel.
  step?: NextStep
  // The risk-assessment note, saved from the checklist (a refusal's text, or null).
  saveRiskNote?: (note: string) => Promise<string | null>
  // Open the court order form as soon as this draft shows.
  openOrderAtStart?: boolean
  // The last refused save: the server's reason, whether it was a conflict, a fresh read.
  saveRefused?: string | null
  saveConflict?: boolean
  reload?: () => Promise<void>
  // The page's sticky action bar: the main button and what stands in its way.
  barSlot?: MainSlot
}
const formOf = (d: StatementDraft | null, data: CaseStatements): DraftFields => ({
  settlementDate: d?.settlementDate ?? undefined,
  damagesReceivedAt: d?.damagesReceivedAt ?? null,
  kind: d?.kind ?? 'FINAL',
  retentionPence: d?.retentionPence ?? 0,
  retentionReason: d?.retentionReason ?? '',
  // A new statement's payee is set on the server by the client's capacity (a
  // child → Court Funds Office…) unless chosen here.
  netDestination: d?.netDestination,
  netDestinationDetail: d?.netDestinationDetail ?? '',
  signatoryUserId: d?.signatoryUserId ?? data.defaultSignatoryId,
  proposed: d?.proposed ?? false,
})

// (Big-audit batch 2, item 9) The approve step, said to someone who CAN
// approve: "You can approve this" — not "An approver … needs to". Everyone
// else keeps the server's wording (who has to act).
export const WAITING_FOR_COLLEAGUE = 'Waiting for a colleague to approve — you prepared it'
export const PREPARE_AND_APPROVE = APPROVE_AND_ISSUE
const listNames = (xs: string[]): string => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`)
export const approverWording = (b: Pick<BlockReason, 'code' | 'reason'>): string => {
  if (b.code === 'NOTES_NOT_REVIEWED') return 'There are notes that haven’t been reviewed (new ones may have appeared since it was approved). You can approve it again — tick “I’ve reviewed these notes” next to Approve.'
  if (b.code !== 'NOT_CHECKED') return b.reason
  if (b.reason.startsWith('An approver (')) return 'You can approve this. Check the figures, the deductions and the wording, then approve it.'
  const withdrawn = b.reason.replace(/so an approver needs to check and approve it again\.$/, 'so it needs approving again — you can approve it.')
  return withdrawn !== b.reason ? withdrawn : `${b.reason} You can approve it.`
}

// (H3) Only what differs from the details as the editor opened them.
const changedFields = (form: DraftFields, opened: DraftFields): DraftFields => {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(form) as (keyof DraftFields)[]) if (JSON.stringify(form[k] ?? null) !== JSON.stringify(opened[k] ?? null)) out[k] = form[k]
  return out as DraftFields
}

const AUTOSAVE_MS = 700
const DraftEditor: React.FC<DraftProps> = ({ data, draft, busy, onFix, save, onEdit, prepare, prepareAndApprove, askApproval, asked, actionError, check, issue, checkMismatch, approveMismatch, preview, recordOrder, recordStage, sendBack, blockedOnIssue, onCancel, figuresUnsaved, sendBackError, step: stepProp, saveRiskNote, openOrderAtStart, saveRefused = null, saveConflict = false, reload, barSlot }) => {
  const id = useId()
  const isNew = !draft
  const [editing, setEditing] = useState(isNew)
  const [form, setForm] = useState<DraftFields>(() => formOf(draft, data))
  // (H3) The details and the row's version AS THE EDITOR OPENED — a reload
  // meanwhile (someone else's save) doesn't move them, so Save is checked
  // against what this person actually edited.
  const [opened, setOpened] = useState<{ form: DraftFields; at: string | undefined }>(() => ({ form: formOf(draft, data), at: draft?.updatedAt }))
  const [touched, setTouched] = useState(false)
  const [datesOk, setDatesOk] = useState({ settlement: true, received: true })
  const [orderOpen, setOrderOpen] = useState(!!openOrderAtStart)
  const [focusReq, setFocusReq] = useState<string | null>(null)
  const [confirmApprove, setConfirmApprove] = useState(false)
  const [sendingBack, setSendingBack] = useState(false)
  const bad = useBadFields()
  // A save was refused: no more tries until the next edit.
  const [stopped, setStopped] = useState(false)
  // Reload / Discard start the dates editor again.
  const [datesKey, setDatesKey] = useState(0)
  const [reloadNonce, setReloadNonce] = useState(0)
  const dirty = editing && touched
  const guard = useUnsavedChangesGuard(dirty)
  // My own save moved the row's version: the next one starts from it. Someone
  // else's move is left alone, so it still refuses (H3).
  const ownSave = useRef(false)
  useEffect(() => {
    if (!draft?.updatedAt || !ownSave.current) return
    ownSave.current = false
    setOpened((o) => ({ ...o, at: draft.updatedAt }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.updatedAt])
  const set = <K extends keyof DraftFields>(k: K, v: DraftFields[K]): void => { setForm((f) => ({ ...f, [k]: v })); setTouched(true); setStopped(false); onEdit?.() }
  // "Reload their version": the details as they now are, nothing of mine left.
  useEffect(() => {
    if (!reloadNonce) return
    const f = formOf(draft, data)
    setForm(f); setOpened({ form: f, at: draft?.updatedAt }); setTouched(false); setStopped(false); bad.reset(); setDatesKey((n) => n + 1); onEdit?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadNonce])

  const childOrPp = knownChildOrPp(draft?.capacity, data)
  const blockers = draft?.blockers ?? []
  const prepared = !!draft?.prepared
  const checked = !!draft?.checked
  const locked = !!draft && (prepared || checked)
  const advisory = draft?.advisory ?? []
  const [reviewed, setReviewed] = useState(false)
  // (Audit fix M10) The tick is for THESE notes on THESE figures: it resets
  // whenever the approval state, the note list or the figures change.
  const noteKey = advisory.map((n) => `${n.code}:${n.message}`).join('|')
  useEffect(() => { setReviewed(false) }, [prepared, checked, noteKey, draft?.approvalFingerprint])
  // Locked meanwhile (prepared elsewhere / a reload): the editors close.
  useEffect(() => { if (locked) { setEditing(false); setOrderOpen(false) } }, [locked])
  // A blocker's button asked for a field: focus it once it's on screen.
  useEffect(() => {
    if (!focusReq) return
    if (!focusById(focusReq)) focusById(FIELD_ID.kind)
    setFocusReq(null)
  }, [focusReq])

  const retentionErr = form.kind === 'INTERIM' && (form.retentionPence ?? 0) > 0 && !(form.retentionReason ?? '').trim() ? 'Say why the retention is held back.' : null
  const receivedErr = form.damagesReceivedAt && form.settlementDate && form.damagesReceivedAt < form.settlementDate ? 'The damages can’t have been received before the settlement date.' : null
  const canSave = !busy && !!form.settlementDate && datesOk.settlement && datesOk.received && !retentionErr && !receivedErr && !bad.any
  const startEdit = (focus?: string): void => {
    if (!editing) { const f = formOf(draft, data); setForm(f); setOpened({ form: f, at: draft?.updatedAt }); setTouched(false); bad.reset() }
    setEditing(true)
    if (focus) setFocusReq(focus)
  }
  const stopEdit = (): void => { setEditing(false); setTouched(false); setStopped(false); bad.reset() }
  const reloadTheirs = async (): Promise<void> => { await reload?.(); setReloadNonce((n) => n + 1) }
  const discard = (): void => { stopEdit(); setDatesKey((n) => n + 1); onEdit?.() }
  const onSave = async (): Promise<void> => {
    if (!canSave) return
    // A new statement sends everything; an existing one only what changed,
    // with the version it was opened from.
    const body = isNew ? form : { ...changedFields(form, opened.form), baseUpdatedAt: opened.at }
    if (!isNew && Object.keys(body).length === 1) { stopEdit(); return } // nothing changed
    if (await save(body)) { setTouched(false); if (!isNew) setEditing(false) }
  }
  const cancel = (): void => guard(() => { if (isNew) onCancel?.(); else stopEdit() })
  // (Settlement redesign) The details save themselves shortly after each change.
  const latestForm = useRef(form)
  latestForm.current = form
  const saveNow = async (): Promise<boolean> => {
    const sent = latestForm.current
    const body = { ...changedFields(sent, opened.form), baseUpdatedAt: opened.at }
    if (Object.keys(body).length === 1) { setTouched(false); return true }
    ownSave.current = true
    const ok = await save(body)
    if (ok) { setOpened((o) => ({ ...o, form: sent })); if (latestForm.current === sent) setTouched(false) } else { ownSave.current = false; setStopped(true) }
    return ok
  }
  useEffect(() => {
    if (isNew || !editing || !touched || !canSave || stopped) return undefined
    const t = window.setTimeout(() => { void saveNow() }, AUTOSAVE_MS)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, touched, editing, canSave, isNew, stopped])
  const done = async (): Promise<void> => { if (!touched || (canSave && await saveNow())) stopEdit() }

  // A child's / protected party's money can't go to the client: never
  // offered (an old draft that still has it shows it, disabled, to change).
  // A NEW statement keeps "Set from the client's age / capacity" on offer.
  const destinations = DESTINATIONS.filter((d) => !(childOrPp === true && d === 'CLIENT'))
  const showAuto = isNew || !form.netDestination

  // ── the actions ──
  // (lib/twoPerson) The server's answer for this viewer: nothing here says "you
  // can approve" unless the server would allow it.
  const approval = draft?.approval
  const canApprove = approval ? approval.allowed : data.canCheck
  const waitingColleague = !!approval?.needsColleague && prepared && !checked
  const offerBoth = canDoItAll(data) && !prepared && !!prepareAndApprove
  const step = stepProp ?? nextStepOf({ data: { ...data, draft }, name: null, figures: null })
  const mainAction = step.main?.action ?? 'none'
  const andIssue = mainAction === 'approveIssue'
  const askLabel = approval?.askable.length ? `Ask ${listNames(approval.askable)} to approve` : null
  const approveReview = advisory.length ? { notesReviewed: true, noteCodes: advisory.map((n) => n.code), notes: advisory.map((n) => ({ code: n.code, message: n.message })) } : undefined
  const FINISH_DETAILS = 'Press Done on the statement details first.'
  const approveBlockedReason = !prepared ? 'Mark it as prepared first.' : advisory.length > 0 && !reviewed ? 'Tick “I’ve reviewed these notes” first.' : dirty ? FINISH_DETAILS : undefined
  // (Follow-ups) Preparing LOCKS the figures, so every fee-earner item must be
  // done first (the server refuses too) — only approval-stage items may remain.
  const toFinish = draft?.prepareBlockers ?? []
  const prepareBlockedReason = dirty ? FINISH_DETAILS
    : figuresUnsaved ? figuresUnsaved
    : toFinish.length && toFinish.every((b) => b.code === 'INTERIM_BILL_UNPAID') ? 'Sort out the unpaid bills above first.'
    : toFinish.length && toFinish.every((b) => b.code === 'INTERIM_BILL_UNPAID' || b.code === 'INTERIM_BILL_UNDELIVERED') ? 'Sort out the interim bills above first.'
    : toFinish.length ? `Can’t ${offerBoth ? 'approve and issue' : 'mark it as prepared'} yet — ${offerBoth ? 'that' : 'preparing'} locks the figures, so first: ${toFinish.map((b) => b.fix.label.replace(/ \(\d+\)$/, '').toLowerCase()).join('; ')}.`
      : undefined
  const bothBlockedReason = prepareBlockedReason ?? (advisory.length > 0 && !reviewed ? 'Tick “I’ve reviewed these notes” first.' : undefined)
  const openOrder = (): void => { setOrderOpen(true); setFocusReq(COURT_ORDER_FIRST_FIELD) }
  const effTarget = (t: FixTarget): FixTarget => (t === 'check' ? (prepared ? 'approve' : 'prepare') : t)
  const actionsFor = (b: BlockReason): ActionBoxAction[] => {
    const t = effTarget(b.fix.target)
    const label = b.fix.label
    if (t === 'prepare' && prepare) {
      if (offerBoth) return [
        { label, kind: 'link', onClick: () => { void prepare() }, disabled: !!prepareBlockedReason || !!busy, disabledReason: prepareBlockedReason },
        { label: PREPARE_AND_APPROVE, kind: 'primary', onClick: () => setConfirmApprove(true), disabled: !!bothBlockedReason || (!!busy && busy !== 'check' && busy !== 'prepare'), disabledReason: bothBlockedReason, busy: busy === 'prepare' || busy === 'check', busyLabel: 'Approving…' },
      ]
      return [{ label, kind: 'primary', onClick: () => { void prepare() }, disabled: !!prepareBlockedReason || (!!busy && busy !== 'prepare'), disabledReason: prepareBlockedReason, busy: busy === 'prepare', busyLabel: 'Marking…' }]
    }
    if (t === 'approve' && check) {
      if (waitingColleague) return askLabel && askApproval && !asked ? [{ label: askLabel, kind: 'primary', onClick: () => { void askApproval() }, disabled: !!busy, busy: busy === 'ask', busyLabel: 'Asking…' }] : []
      if (!canApprove) return []
      return [{ label, kind: 'primary', onClick: () => setConfirmApprove(true), disabled: !!approveBlockedReason || (!!busy && busy !== 'check'), disabledReason: approveBlockedReason, busy: busy === 'check', busyLabel: 'Approving…' }]
    }
    // (Audit B2, H3) The court's decision is recorded in the court steps.
    if (t === 'courtSteps' && childOrPp && recordStage) {
      return [{ label, kind: 'link', arrow: true, onClick: () => { const el = document.getElementById(COURT_STEPS_ID); el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); el?.focus?.({ preventScroll: true }) } }]
    }
    if (t === 'courtOrder' && childOrPp && recordOrder) {
      if (orderOpen) return [] // the form is open below — its own button records it
      return [{ label, kind: 'link', arrow: true, onClick: openOrder, disabled: locked || !!busy, disabledReason: locked ? 'Send it back for changes first.' : undefined }]
    }
    if (FIELD_TARGETS.has(t) && draft) {
      const field = t === 'statement' ? 'settlementDate' : (t as FieldTarget)
      return [{ label, kind: 'link', arrow: true, onClick: () => startEdit(FIELD_ID[field]), disabled: locked, disabledReason: locked ? 'Send it back for changes first.' : undefined }]
    }
    return [{ label, kind: 'link', arrow: true, onClick: () => onFix(b.fix.target) }]
  }
  // (Audit fix batch 2) The checklist rows: each reason, its action on the
  // right of its own row; the maker / checker steps already done are ticked
  // ("1 of 3 done").
  // (Big-audit batch 2, item 9) The approver's "I've reviewed these notes"
  // tick sits right beside the Approve button (the notes box keeps the list).
  // Approved already: no tick, unless new notes mean it needs approving again.
  const reviewTick = advisory.length > 0 && (prepared || offerBoth) && canApprove && check && (!checked || (!!draft && notesOutOfDate(draft))) ? (
    <label className="inline-flex items-center gap-[8px] text-[13px] font-semibold text-casevo-forest" data-review-tick="">
      <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />I’ve reviewed these notes
    </label>
  ) : null
  const itemsOf = (list: BlockReason[], withDone = false, withTick = true): ActionBoxItem[] => [
    ...(withDone && prepared ? [{ key: 'done-prepared', done: true, message: 'Marked as prepared', hint: draft?.prepared?.by ? `By ${draft.prepared.by}` : undefined }] : []),
    ...(withDone && checked ? [{ key: 'done-approved', done: true, message: 'Approved', hint: draft?.checked?.by ? `By ${draft.checked.by}` : undefined }] : []),
    ...list.map((b) => {
      const isApprove = effTarget(b.fix.target) === 'approve'
      const actions = actionsFor(b)
      const preparing = effTarget(b.fix.target) === 'prepare' && offerBoth
      const waiting = isApprove && !canApprove && !waitingColleague
      const tick = (isApprove || preparing) && withTick && actions.length > 0 ? reviewTick : null
      const askedNote = isApprove && waitingColleague && asked ? `Asked ${listNames(asked)}.` : undefined
      return { key: b.code, message: isApprove && waitingColleague ? WAITING_FOR_COLLEAGUE : isApprove && canApprove ? approverWording(b) : b.reason, hint: askedNote ?? (waiting ? 'Waiting for an approver.' : tick ?? undefined), actions }
    }),
  ]
  // A paid interim bill never marked delivered: a row to deal with, not a block.
  const paidUndelivered: ActionBoxItem[] = (draft?.warnings ?? []).filter((w) => w.code === 'INTERIM_BILL_PAID_UNDELIVERED').map((w) => ({
    key: w.code, message: w.message, hint: 'This doesn’t stop the statement being prepared or issued.',
    actions: actionsFor({ code: w.code, reason: w.message, fix: { label: 'Open Billing', target: 'billing' } }),
  }))
  const hasCourtBlocker = [...blockers, ...(blockedOnIssue ?? [])].some((b) => b.fix.target === 'courtOrder')

  const signatory = data.signatories.find((o) => o.id === (draft?.signatoryUserId ?? null))
  const addressee = draft?.addressee?.name ?? (childOrPp && data.litigationFriendName ? `${data.litigationFriendName} (litigation friend)` : null)

  // ── the one main button ──
  const datesGap = !!draft && datesMissing(draft)
  const real = (draft ? realBlockers(draft) : []).filter((b) => !(datesGap && b.code === 'NO_DAMAGES_RECEIVED_DATE') && !(mainAction === 'createCfa' && b.code === 'CALCULATION_NO_CFA'))
  const held = draft ? issueHeldByBill(draft) : null
  const nothingSavedYet = draft?.id === 'pending'
  const finishBlocked = real.length ? 'Fix the items above first.' : undefined
  const mainId = useId()
  const mainWhyNot = ((): string | undefined => {
    if (datesGap && (mainAction === 'prepare' || mainAction === 'approveIssue' || mainAction === 'approve')) return 'Add the dates first.'
    if (figuresUnsaved && (mainAction === 'prepare' || mainAction === 'approveIssue' || mainAction === 'approve' || mainAction === 'issue')) return figuresUnsaved
    if (mainAction === 'prepare') return prepareBlockedReason
    if (mainAction === 'approveIssue') return (prepared ? approveBlockedReason : bothBlockedReason) ?? finishBlocked
    if (mainAction === 'approve') return prepared ? approveBlockedReason : bothBlockedReason
    if (mainAction === 'issue') return real.length ? 'Fix the items above first.' : dirty ? FINISH_DETAILS : held ?? undefined
    return undefined
  })()
  const mainBusy = mainAction === 'prepare' ? busy === 'prepare' : mainAction === 'ask' ? busy === 'ask' : mainAction === 'issue' ? busy === 'issue' : busy === 'prepare' || busy === 'check' || busy === 'issue'
  const mainBusyLabel = mainAction === 'prepare' ? 'Marking…' : mainAction === 'ask' ? 'Asking…' : busy === 'issue' ? 'Issuing…' : 'Approving…'
  const pressMain = (): void => {
    if (mainAction === 'prepare') void prepare?.()
    else if (mainAction === 'approveIssue' || mainAction === 'approve') setConfirmApprove(true)
    else if (mainAction === 'ask') void askApproval?.()
    else if (mainAction === 'issue') issue?.()
    else if (mainAction === 'addFigures') onFix('settlementInputs')
    else if (mainAction === 'createCfa') onFix('createCfa')
  }
  // The approve step's own reason, said plainly above the button.
  const ownStep = blockers.find((b) => effTarget(b.fix.target) === 'approve')
  const title = !draft ? '' : checked ? (notesOutOfDate(draft) ? 'New notes to review' : 'Ready to issue')
    : waitingColleague ? WAITING_FOR_COLLEAGUE
    : prepared ? (canApprove ? 'Ready for you to approve' : 'Waiting for an approver')
    : real.length || datesGap ? 'Nearly ready' : canDoItAll(data) ? 'Ready to approve and issue' : 'Ready to mark as prepared'
  const showMain = !!step.main && (mainAction !== 'ask' || !asked)
    && (mainAction !== 'approveIssue' || prepared || !!prepareAndApprove || !!prepare)

  return (
    <div className="flex flex-col gap-4">
      {draft && <h3 className="m-0 text-[16px] font-semibold text-casevo-forest">{title}</h3>}
      {draft?.carriedDispute && <CarriedDisputeNote d={draft.carriedDispute} />}
      {draft?.preparationWithdrawn && !prepared && (
        <Banner tone="warn" title="Figures changed after it was prepared">The preparation{draft.approvalWithdrawn ? '' : ' (and any approval)'} was withdrawn on {ukDate(String(draft.preparationWithdrawn.at).slice(0, 10))}: {draft.preparationWithdrawn.reason} Check the figures and mark it as prepared again.</Banner>
      )}
      {draft?.approvalWithdrawn && !prepared && !draft.preparationWithdrawn && (
        <Banner tone="warn" title="Figures changed after approval">The approval was withdrawn on {ukDate(String(draft.approvalWithdrawn.at).slice(0, 10))}: {draft.approvalWithdrawn.reason} Check the figures and mark it as prepared again.</Banner>
      )}
      {draft?.sentBack && !prepared && (
        <Banner tone="info" title="Sent back for changes">Sent back by {draft.sentBack.by} on {ukDate(String(draft.sentBack.at).slice(0, 10))} — ‘{draft.sentBack.reason}’</Banner>
      )}

      <section aria-label="Statement details" className="flex flex-col gap-[10px]">
        <div className="flex items-center gap-[8px] flex-wrap">
          <h4 className="m-0 flex-1 text-[13.5px] font-semibold text-casevo-forest">{isNew ? 'New statement' : 'Statement details'}</h4>
          {draft && !editing && !locked && !nothingSavedYet && <EditPencil label="Edit the statement details" disabled={!!busy} onClick={() => startEdit()} />}
          {locked && <span className="inline-flex items-center gap-[5px] text-[12.5px] text-casevo-text-soft"><BIcon name="lock" size={12} />Locked — {checked ? 'approved' : 'prepared'}</span>}
        </div>

        {!editing && draft ? (
          <Fold labelClass="text-[13px] text-casevo-text-soft" dataKey="statement-details"
            label={[draft.settlementDate ? `Settled ${ukDate(draft.settlementDate)}` : datesGap ? null : 'settlement date not recorded', draft.damagesReceivedAt ? `received ${ukDate(draft.damagesReceivedAt)}` : datesGap ? null : 'date received not recorded', draft.kind === 'INTERIM' ? 'interim' : 'final', `signed by ${signatory?.name ?? draft.signatoryName ?? 'nobody yet'}`].filter(Boolean).join(' · ')}>
          <dl className="m-0 grid gap-[10px] sm:grid-cols-2">
            <SummaryRow label="Settlement date">{draft.settlementDate ? ukDate(draft.settlementDate) : '—'}</SummaryRow>
            <SummaryRow label="Damages received">{draft.damagesReceivedAt ? ukDate(draft.damagesReceivedAt) : 'Not recorded'}</SummaryRow>
            <SummaryRow label="Statement">{draft.kind === 'INTERIM' ? 'Interim' : 'Final'}</SummaryRow>
            <SummaryRow label="Signed by">{signatory?.name ?? draft.signatoryName ?? 'Not chosen'}</SummaryRow>
            {draft.kind === 'INTERIM' && <SummaryRow label="Retention held back">{money(draft.retentionPence)}{draft.retentionReason ? ` — ${draft.retentionReason}` : ''}</SummaryRow>}
            <SummaryRow label="Where the net sum goes">{DESTINATION_LABEL[draft.netDestination]}{draft.netDestinationDetail ? <span className="block text-[12.5px] text-casevo-text-soft">{draft.netDestinationDetail}</span> : null}</SummaryRow>
            {childOrPp && <SummaryRow label="Issue as PROPOSED">{draft.proposed ? 'Yes — subject to the court’s approval' : 'No'}</SummaryRow>}
            {childOrPp && <SummaryRow label="Addressed to">{addressee ?? 'The litigation friend (not recorded yet)'}</SummaryRow>}
          </dl>
          </Fold>
        ) : (
          <>
            <div className="grid gap-[14px] sm:grid-cols-2">
              <FormField label="Settlement date" required variant="info">
                <div id={FIELD_ID.settlementDate}>
                  <DateInput value={form.settlementDate ?? null} onChange={(v) => set('settlementDate', v ?? undefined)} ariaLabel="Settlement date" allowFuture={false}
                    onValidityChange={(ok) => setDatesOk((d) => ({ ...d, settlement: ok }))} />
                </div>
              </FormField>
              <FormField label="Date the damages were received" variant="info" helper="Shown at the top of the statement.">
                <div id={FIELD_ID.damagesReceivedAt}>
                  <DateInput value={form.damagesReceivedAt ?? null} onChange={(v) => set('damagesReceivedAt', v)} ariaLabel="Date the damages were received" allowFuture={false}
                    onValidityChange={(ok) => setDatesOk((d) => ({ ...d, received: ok }))} />
                </div>
                <FieldError message={receivedErr ?? undefined} />
              </FormField>
              <FormField label="Statement" variant="info" htmlFor={FIELD_ID.kind}>
                <select id={FIELD_ID.kind} className={SELECT} value={form.kind} onChange={(e) => set('kind', e.target.value as DraftFields['kind'])}>
                  <option value="FINAL">Final</option>
                  <option value="INTERIM">Interim</option>
                </select>
              </FormField>
              <FormField label="Signed by" required variant="info" htmlFor={FIELD_ID.signatory}>
                <select id={FIELD_ID.signatory} className={SELECT} value={form.signatoryUserId ?? ''} onChange={(e) => set('signatoryUserId', e.target.value || null)}>
                  <option value="">Choose…</option>
                  {data.signatories.map((o) => <option key={o.id} value={o.id}>{o.name}{o.jobTitle ? `, ${o.jobTitle}` : ''}{o.isFeeEarner ? ' (fee earner)' : ''}</option>)}
                </select>
              </FormField>
              {form.kind === 'INTERIM' && (
                <>
                  <PenceField label="Retention held back" pence={form.retentionPence ?? 0} onPence={(p) => set('retentionPence', p ?? 0)} onBadChange={bad.track('retention')} />
                  <FormField label="Why it’s held back" required={(form.retentionPence ?? 0) > 0} variant="info" htmlFor={FIELD_ID.retentionReason}>
                    <input id={FIELD_ID.retentionReason} className={INPUT} value={form.retentionReason ?? ''} maxLength={500} onChange={(e) => set('retentionReason', e.target.value)}
                      aria-invalid={!!retentionErr || undefined} aria-describedby={retentionErr ? `${id}-ret-err` : undefined} />
                    <span id={`${id}-ret-err`}><FieldError message={retentionErr ?? undefined} /></span>
                  </FormField>
                </>
              )}
              {childOrPp && (
                <div className="sm:col-span-2 rounded-[10px] border border-casevo-border px-[12px] py-[10px]">
                  <label className="inline-flex items-start gap-[8px] text-[13.5px] text-casevo-forest">
                    <input type="checkbox" className="mt-[3px]" checked={!!form.proposed} onChange={(e) => set('proposed', e.target.checked)} />
                    <span><b className="font-semibold">Issue as PROPOSED (subject to the court’s approval)</b>
                      <span className={`${NOTE} block`}>For the litigation friend and the approval hearing, before the court order is recorded. It has no agreement block, can’t be approved by the client and is never ready for client account. Once the order is recorded, the final statement is a new version.</span></span>
                  </label>
                </div>
              )}
              <FormField label="Where the net sum goes" variant="info" htmlFor={FIELD_ID.netDestination}>
                <select id={FIELD_ID.netDestination} className={SELECT} value={form.netDestination ?? ''} onChange={(e) => set('netDestination', (e.target.value || undefined) as NetDestination | undefined)}>
                  {showAuto && <option value="">Set from the client’s age / capacity</option>}
                  {childOrPp === true && form.netDestination === 'CLIENT' && <option value="CLIENT" disabled>To the client (not for a child or protected party)</option>}
                  {destinations.map((d) => <option key={d} value={d}>{DESTINATION_LABEL[d]}</option>)}
                </select>
              </FormField>
              {form.netDestination && <FormField label={DESTINATION_DETAIL_LABEL[form.netDestination]} variant="info" htmlFor={`${id}-detail`}>
                <input id={`${id}-detail`} className={INPUT} value={form.netDestinationDetail ?? ''} maxLength={300} onChange={(e) => set('netDestinationDetail', e.target.value)}
                  placeholder={form.netDestination === 'COURT_FUNDS_OFFICE' ? 'e.g. CFO account reference' : form.netDestination === 'DEPUTY' ? 'e.g. Jane Smith, deputy (CoP order 12345)' : ''} />
              </FormField>}
            </div>
            {!isNew && (
              <div className="flex items-center gap-[10px] flex-wrap">
                <button type="button" className={B_SECONDARY} disabled={!!busy || (touched && !canSave)} onClick={() => { void done() }}>Done</button>
                {touched && <button type="button" className={QUIET} disabled={busy === 'save'} onClick={discard}>Discard my change</button>}
                {bad.any && <span className={NOTE}>Correct the amount first.</span>}
                {!bad.any && <span className={NOTE} role="status" data-autosave="">{busy === 'save' ? 'Saving…' : stopped ? 'Not saved' : touched ? 'Saves as you go' : 'Saved'}</span>}
              </div>
            )}
          </>
        )}
        {saveRefused && draft && (
          <div data-save-refused="">
            <ErrorNote action={saveConflict ? <button type="button" className={QUIET} onClick={() => { void reloadTheirs() }}>Reload their version</button> : undefined}>{saveRefused}</ErrorNote>
          </div>
        )}
      </section>

      {draft && (
        <>
          {draft.warnings.filter((w) => w.code === 'CHILD_OVER_25K_SUMMARY' || w.code === 'COURT_OF_PROTECTION' || w.code === 'RECOVERED_ABOVE_BASE').map((w) => <p key={w.code} className="m-0 rounded-[10px] bg-casevo-input px-[12px] py-[8px] text-[13px] text-casevo-forest" data-calm-note={w.code}>{w.message}</p>)}
          {childOrPp && recordOrder && recordStage && (
            <CourtOrderPanel draft={draft} stages={data.courtStages} busy={busy} today={data.today} open={orderOpen} setOpen={setOrderOpen}
              locked={locked} hideOpenButton={hasCourtBlocker} recordOrder={recordOrder} recordStage={recordStage}
              previousOrder={draft.supersedes ? data.statements.find((s) => s.id === draft.supersedes?.id)?.courtOrder ?? null : null} />
          )}
          {draft.billMismatch && checkMismatch && approveMismatch && (
            <IssueMismatchCheck key={draft.billMismatch.key} draft={draft} canApprove={data.canCheck} onlyApprover={data.onlyApprover || data.twoPersonChecks === false} busy={busy}
              record={checkMismatch} approve={approveMismatch} />
          )}
          {advisory.length > 0 && (
            <ApproverNotes notes={advisory} locked={locked} busy={!!busy} onFix={onFix} saveRiskNote={saveRiskNote}
              reviewedLine={draft.notesReviewed ? `Reviewed by ${draft.notesReviewed.by} on ${ukDate(String(draft.notesReviewed.at).slice(0, 10))}.` : null} />
          )}
          {paidUndelivered.length > 0 && (
            <ul className="m-0 p-0 list-none text-[13.5px] text-casevo-forest" aria-label="Interim bills">
              {paidUndelivered.map((p) => (
                <li key={p.key} className="flex items-baseline justify-between gap-[12px]" data-calm-note={p.key}>
                  <span className="min-w-0">{p.message}<span className={`${NOTE} block`}>{p.hint}</span></span>
                  <button type="button" className="text-[13px] font-semibold text-casevo-link whitespace-nowrap focus:outline-none casevo-focus rounded-[4px]" onClick={() => onFix('billing')}>Open Billing</button>
                </li>
              ))}
            </ul>
          )}
          {ownStep && <p className={`${NOTE} m-0`} data-own-step="">{waitingColleague ? (asked ? `Asked ${listNames(asked)}.` : 'The figures are locked while it waits. Nothing is needed from you.') : canApprove ? approverWording(ownStep) : ownStep.reason}{!waitingColleague && !canApprove ? <span className="block">Waiting for an approver.</span> : null}</p>}
          {prepared && !checked && !ownStep && !canApprove && <p className={`${NOTE} m-0`}>Waiting for an approver to check it.</p>}

          {datesGap && !editing && (
            <DatesEditor key={datesKey} draft={draft} busy={!!busy} onEdit={onEdit} save={(dates) => save({ ...dates, baseUpdatedAt: draft.updatedAt })} refused={stopped => { setStopped(stopped) }} onDiscard={discard} />
          )}
        </>
      )}

      {/* The action bar: what stands in the way, the one main button, the quiet
          actions beside it, the trail. It grows upwards, so the button stays put. */}
      {isNew && inSlot(barSlot, (
        <div className="flex flex-wrap-reverse items-center gap-x-[10px] gap-y-[4px]" data-main-area="">
          <button type="button" className={B_PRIMARY} data-main-action="createDraft" disabled={!canSave} onClick={() => { void onSave() }}>{busy === 'save' ? 'Saving…' : 'Create the draft'}</button>
          <button type="button" className={B_SECONDARY} onClick={cancel}>Cancel</button>
          {bad.any && <span className={NOTE}>Correct the amount first.</span>}
        </div>
      ))}
      {draft && inSlot(barSlot, (
          <div role="group" aria-label="Before it can be issued" className="flex flex-col gap-[8px]" data-main-area="">
            {blockedOnIssue && blockedOnIssue.length > 0
              // The server's reason, always. The button below does the approve / prepare step.
              ? <BarRows live ariaLabel="The statement can’t be issued yet" title="The statement can’t be issued yet" items={blockedOnIssue.map((b) => {
                  const own = effTarget(b.fix.target) === 'approve' || effTarget(b.fix.target) === 'prepare'
                  return { key: b.code, message: own && canApprove ? approverWording(b) : b.reason, actions: own ? [] : actionsFor(b) }
                })} />
              : real.length > 0 && <BarRows ariaLabel="To issue this statement" items={itemsOf(real, false, false)} />}
            {actionError && <div data-step-error=""><ErrorNote>{actionError}</ErrorNote></div>}
            {reviewTick}
            {mainWhyNot && <p id={mainId} className={`${NOTE} m-0`} data-main-why="">{mainWhyNot}</p>}
            <div className="flex flex-wrap-reverse items-center gap-x-[12px] gap-y-[4px]" data-main-row="">
              {showMain && step.main && (
                <button type="button" className={B_PRIMARY} data-main-action={mainAction} aria-busy={mainBusy || undefined} aria-describedby={mainWhyNot ? mainId : undefined}
                  disabled={!!mainWhyNot || (!!busy && !mainBusy) || mainBusy} onClick={pressMain}>{mainBusy ? mainBusyLabel : step.main.label}</button>
              )}
              {preview && <button type="button" className={QUIET} disabled={!!busy} onClick={preview}>Preview</button>}
              {locked && sendBack && <button type="button" id={SEND_BACK_ID} className={QUIET} disabled={!!busy} onClick={() => setSendingBack(true)}>Send back for changes</button>}
            </div>
            <StepTrail trail={step.trail} />
          </div>
      ))}
      {draft && (
        <>
          {approval?.selfBasis === 'ONLY_APPROVER' && <p className={`${NOTE} m-0`}>You’re the firm’s only approver, so you can approve a statement you prepared. That is recorded in the history.</p>}
          {approval?.selfBasis === 'CHECKS_OFF' && <p className={`${NOTE} m-0`}>Two-person checks are off at this firm, so you can approve a statement you prepared. That is recorded in the history.</p>}
          {draft.checked?.selfNote && <p className={`${NOTE} m-0`} data-self-approved="">{draft.checked.selfNote}.</p>}
        </>
      )}

      {confirmApprove && check && (
        <ConfirmDialogue isOpen title={andIssue ? `${step.main?.label ?? APPROVE_AND_ISSUE} this statement?` : 'Approve this statement?'} confirmLabel={andIssue ? step.main?.label ?? APPROVE_AND_ISSUE : 'Approve'} tone="primary"
          message={`You confirm the figures, the deductions and the wording shown are right${advisory.length ? ', and that you have reviewed the notes for the approver' : ''}.${andIssue ? ' This gives the statement its number, freezes the figures, the wording and the signatory, and saves the PDF to the case. An issued statement can’t be changed — a correction is a new version.' : ' It can then be issued.'}`}
          onCancel={() => setConfirmApprove(false)}
          onConfirm={() => { setConfirmApprove(false); void (!prepared && prepareAndApprove ? prepareAndApprove(approveReview, andIssue) : check(approveReview, andIssue)) }} />
      )}
      {sendingBack && sendBack && <SendBackDialog busy={busy === 'sendBack'} error={sendBackError ?? null} onClose={() => setSendingBack(false)} onSend={sendBack} />}
    </div>
  )
}

export default DraftEditor
